<template>
	<div class="relative flex items-center">
		<div
			class="pointer-events-none absolute inset-y-0 left-sm flex items-center"
		>
			<v-icon icon="tag" class="text-xl" />
		</div>

		<div class="flex-1">
			<v-input
				v-model="input"
				type="text"
				size="sm"
				:readonly="
					!!properties.cart.promotionCode || !!properties.cart.giftCard
				"
				:placeholder="t('type-code')"
				class="pr-16 pl-lg lg:pr-20"
			/>
		</div>

		<div class="absolute inset-y-0 right-sm flex items-center space-x-4">
			<v-button
				v-if="
					input.length > 0 &&
					!properties.cart.promotionCode &&
					!properties.cart.giftCard
				"
				size="sm"
				class="underline hover:text-red-500 focus:text-red-500 active:text-red-700"
				@click="save"
			>
				{{ t("activate") }}
			</v-button>

			<template
				v-else-if="properties.cart.promotionCode || properties.cart.giftCard"
			>
				<v-button
					size="sm"
					class="px-0 underline hover:text-red-500 focus:text-red-500 active:text-red-700"
					@click="clear"
				>
					{{ t("deactivate") }}
				</v-button>

				<v-icon icon="code-active" class="text-2xl" />
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import {type ViewCartQuery} from "@pkg/shared/graphql";
import {useToast} from "@pkg/shared/store/toast";

import {useCart} from "@pkg/shared/composables/use-cart";

const properties = defineProps<{
	cart: Extract<ViewCartQuery["cart"], {__typename: "Cart"}>;
}>();

const {t} = useI18n();
const toast = useToast();
const input = ref<string>(
	properties.cart.promotionCode?.code ?? properties.cart.giftCard?.code ?? "",
);
const cartService = useCart();

async function save() {
	let promoApplied = false;
	try {
		promoApplied = await cartService.setPromotionCode(input.value);
	} catch (e) {
		// eslint-disable-next-line no-console
		console.warn(e);
	}
	if (promoApplied) {
		toast.add("success", t("toast.code-activated"));
		return;
	}

	let giftApplied = false;
	try {
		giftApplied = await cartService.setGiftCardCode(input.value);
	} catch (e) {
		// eslint-disable-next-line no-console
		console.warn(e);
	}
	if (giftApplied) {
		toast.add("success", t("toast.gift-card-activated"));
		return;
	}

	toast.add("warning", t("toast.code-error"));
}

async function clear() {
	await cartService.clearPromotionCode();
	await cartService.clearGiftCard();

	input.value = "";

	toast.add("success", t("toast.code-deactivated"));
}
</script>

<i18n lang="json">
{
	"et": {
		"type-code": "Sooduskood voi kinkekaart",
		"activate": "Rakenda",
		"deactivate": "Lähtesta"
	},
	"fi": {
		"type-code": "Kampanjakoodi tai lahjakortti",
		"activate": "Aktivoi",
		"deactivate": "Nollaa"
	}
}
</i18n>
